<template>
    <div class="px-3 driver-messages-page">
        <div class="v-container">
            <v-row>
                <v-col>
                    <v-tabs v-if="showMpcAvvik" v-model="viewSelection" height="30" class="selection-tabs">
                        <v-tab key="mpc" data-id="mpc" @click="navigateMpc()"> MPC </v-tab>
                        <v-tab key="avvik" data-id="avvik"> Avvik </v-tab>
                    </v-tabs>
                </v-col>
                <v-col>
                    <div class="date body-2 text-right mr-2">
                        {{ date(baseStore.selectedDate) }}
                    </div>
                </v-col>
            </v-row>
            <trip-info v-bind="tripInfo" />
        </div>

        <div class="v-container">
            <v-row align="center" justify="center">
                <v-col cols="12">
                    <radio-button-list :items="subjectItems" inline @update="changeSubject($event)" />
                </v-col>
            </v-row>
            <template v-if="selectedCategory">
                <v-row>
                    <v-col>
                        <v-divider />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="hasMessageItems" cols="12" data-id="template-items">
                        <radio-button-list :items="messageItems" inline @update="changeMessage($event)" />
                    </v-col>
                </v-row>
                <v-row v-if="hasBody">
                    <v-col>
                        <v-divider />
                    </v-col>
                </v-row>
                <v-row v-if="hasBody">
                    <v-col v-if="hasInputItems" cols="6" data-id="input-items">
                        <message-input v-for="(inputItem, i) in inputItems" :key="i" :item="inputItem" @input="handleInput" />
                    </v-col>
                    <v-col v-for="(choiceItems, i) in groupedChoiceItems" :key="i" :cols="bodyCols">
                        <h2 v-if="choiceItems[0].groupLabel" :class="bodyCols === 12 ? 'text-center' : ''" class="mb-2">
                            {{ choiceItems[0].groupLabel }}
                        </h2>
                        <radio-button-list
                            :items="choiceItems"
                            :group="choiceItems[0].group"
                            :inline="isInlineChoiceItems"
                            @update="selectChoiceItem($event)"
                        />
                    </v-col>
                </v-row>
            </template>
            <v-row class="nav-controls">
                <v-col :cols="selectedSubject ? 6 : 12">
                    <v-btn large block class="text-none back-btn" data-id="go-back" @click="navigateBack"> Tilbake til avganger </v-btn>
                </v-col>
                <v-col v-if="selectedSubject" cols="6">
                    <v-btn :disabled="disabled" color="primary" large block class="text-none" data-id="send" @click="sendMessage">
                        Send
                    </v-btn>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script setup lang="ts">
import RadioButtonList from '@/features/shared/RadioButtonList.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TripInfo from '@/features/trips/TripInfo.vue';
import MessageInput from '@/features/messages/MessageInput.vue';
import _ from 'lodash';
import { useEmitter, Events } from '@/plugins/emitter';
import useNavigate from '@/plugins/navigate';
import { ButtonListItem } from '@/features/shared/models/ButtonListItem';
import { CategoryTemplate, MessageChoiceTemplate, MessageInputTemplate, MessageTemplate } from '@/features/messages/MessageTemplate';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Views, ViewIndex } from '@/helpers/ViewSwitcher';
import { useBaseStore } from '@/features/shared/baseStore';
import { useLinesStore } from '@/features/lines/linesStore';
import { useSettingsStore } from '@/features/shared/settingsStore';
import { useTripsStore } from '@/features/trips/tripsStore';
import { useMessagesStore } from '@/features/messages/messagesStore';
import { useDateTimeFilters } from '@/helpers/useDateTimeFilters';

const baseStore = useBaseStore();
const settingsStore = useSettingsStore();
const linesStore = useLinesStore();
const tripsStore = useTripsStore();
const messagesStore = useMessagesStore();
const { navigate } = useNavigate();
const router = useRouter();
const route = useRoute();
const eventbus = useEmitter();
const { date } = useDateTimeFilters();

const subjectItems = ref<ButtonListItem[]>([]);
const messageItems = ref<ButtonListItem[]>([]);
const groupedChoiceItems = ref([]);
const selectedChoiceItems = ref([]);
const inputItems = ref(new Array<MessageInputTemplate>());
const selectedSubject = ref<MessageTemplate | null>(null);
const selectedCategory = ref<CategoryTemplate | null>(null);
const viewSelection = ref<number | null>(1);
const disabled = ref(true);

interface Props {
    lineNumber: string;
    tripId: string;
}
const props = defineProps<Props>();

eventbus.emitter.emit(Events.menuUpdate, 'trip');

const categoryPrefix = 'cat';
const messagePrefix = 'msg';

const showMpcAvvik = computed(() => {
    return settingsStore.settings && settingsStore.showMpcAvvik;
});

const showMpc = computed(() => {
    return settingsStore.settings && settingsStore.showMpc;
});

const showAvvik = computed(() => {
    return settingsStore.settings && settingsStore.showAvvik;
});

const hasBody = computed(() => {
    return hasInputItems.value || hasChoiceItems.value;
});

const bodyCols = computed(() => {
    if (hasInputItems.value) {
        return 6;
    }
    const groupCount = groupedChoiceItems.value.length;
    return groupCount === 0 ? 12 : 12 / groupCount;
});

const isInlineChoiceItems = computed(() => {
    return groupedChoiceItems.value.length === 1 && !hasInputItems.value;
});

const hasInputItems = computed(() => {
    return inputItems.value.length > 0;
});

const hasMessageItems = computed(() => {
    return messageItems.value.length > 0;
});

const hasChoiceItems = computed(() => {
    return groupedChoiceItems.value.length > 0 && groupedChoiceItems.value[0].length > 0;
});

const currentLine = computed(() => {
    return linesStore.getLineByNumber(baseStore.lineNumber);
});

const lineName = computed(() => {
    return currentLine.value?.name ?? '';
});

const trip = computed(() => {
    return tripsStore.trips.find(x => x.tripId === props.tripId);
});

const tripInfo = computed(() => {
    if (!trip.value || !currentLine.value) {
        return {
            name: null,
            origin: null,
            destination: null,
            originDeparture: null
        };
    }

    return {
        name: currentLine.value.name,
        origin: trip.value.originQuayName,
        destination: trip.value.destination,
        originDeparture: trip.value.originDeparture
    };
});

async function initialize() {
    if (showMpc.value && !showAvvik.value) {
        navigateMpc();
    }
    baseStore.lineNumber = props.lineNumber;
    await fetchTrips();
    await messagesStore.fetchTemplates();
    setSubjectItems();
    baseStore.tripPage = { pageName: 'Tur', urlName: 'messages' };
    baseStore.departuresPage = { pageName: `Avganger (${lineName.value})`, urlName: 'departures' };
}

function setSubjectItems() {
    const categoryItems = getCategories()
        .filter(x => x.messageTemplates.filter(y => !y.isFirstLevel).length > 0)
        .map(x => createCategoryItem(x.name, x.label));
    const messageItems = getCategories()
        .flatMap(x => x.messageTemplates)
        .filter(x => x.isFirstLevel)
        .map(x => createMessageItem(x.id, x.label));

    subjectItems.value = categoryItems.concat(messageItems);
}

function createCategoryItem(name: string, label: string) {
    return { value: `${categoryPrefix}:${name}`, label, group: 'subject', groupLabel: '' };
}

function createMessageItem(id: string, label: string) {
    return { value: `${messagePrefix}:${id}`, label, group: 'subject', groupLabel: '' };
}

function getCategories(): CategoryTemplate[] {
    return _.chain(messagesStore.templates)
        .groupBy(x => x.categoryName)
        .map((messages, category) => ({ name: category, label: messages[0].categoryLabel, messageTemplates: messages }))
        .value();
}

async function fetchTrips() {
    await tripsStore.fetchTrips({
        lineNumber: baseStore.lineNumber,
        calendarDay: baseStore.calendarDay,
        backgroundSync: false
    });
}

function handleInput(data) {
    const i = inputItems.value.findIndex(item => item.name === data.name);
    inputItems.value[i].value = data.value;
    checkFormSelection();
}

function checkFormSelection() {
    let validInput = true; // if no input fields, no need to check for value
    if (inputItems.value.length > 0) {
        validInput = true;
        inputItems.value.forEach(item => {
            if (!item.value) {
                validInput = false;
            }
        });
    }

    disabled.value = !(selectedChoiceItems.value.length === groupedChoiceItems.value.length && validInput);
}

function navigateBack() {
    navigate({ name: 'departures', params: { lineNumber: props.lineNumber } });
}

function changeSubject(selectedItem: ButtonListItem) {
    resetSecondLevel();
    resetThirdLevel();

    if (selectedItem.value.startsWith(categoryPrefix)) {
        const selectedValue = selectedItem.value.split(':').pop();
        selectedCategory.value = getCategories().find(x => x.name === selectedValue);
        onSelectedCategoryChanged(selectedCategory.value);
    }
    if (selectedItem.value.startsWith(messagePrefix)) {
        resetThirdLevel();
        const selectedValue = selectedItem.value.split(':').pop();
        selectedSubject.value = messagesStore.templates.find(x => x.id === selectedValue);
        onSelectedSubjectChanged(selectedSubject.value);
    }
    checkFormSelection();
}

function changeMessage(selectedItem: ButtonListItem) {
    resetThirdLevel();

    const selectedValue = selectedItem.value;
    selectedSubject.value = messagesStore.templates.find(x => x.id === selectedValue);
    onSelectedSubjectChanged(selectedSubject.value);
    checkFormSelection();
}

function onSelectedCategoryChanged(newVal: CategoryTemplate) {
    messageItems.value = newVal.messageTemplates
        .filter(x => !x.isFirstLevel)
        .map(x => ({ value: x.id, label: x.label, group: 'message', groupLabel: '' }));
}

function onSelectedSubjectChanged(newVal: MessageTemplate) {
    groupedChoiceItems.value = _(newVal.choiceTemplates)
        .groupBy((template: MessageChoiceTemplate) => template.group)
        .map((templates: MessageChoiceTemplate[]): ButtonListItem[] =>
            templates.map(
                (template: MessageChoiceTemplate): ButtonListItem => ({
                    label: template.label,
                    value: template.name,
                    group: template.group,
                    groupLabel: template.groupLabel
                })
            )
        )
        .value();

    inputItems.value = newVal.inputTemplates;
    inputItems.value.forEach(item => {
        item.value = '0';
    });
}

function selectChoiceItem(selectedItem: ButtonListItem) {
    const newSelectedItems = selectedChoiceItems.value.filter(x => x.group !== selectedItem.group);
    newSelectedItems.push(selectedItem);
    selectedChoiceItems.value = newSelectedItems;
    checkFormSelection();
}

function sendMessage() {
    messagesStore.sendMessage({
        templateId: selectedSubject.value.id,
        input: Object.fromEntries(inputItems.value.map((x): [string, string] => [x.name, x.value])),
        choice: Object.fromEntries(selectedChoiceItems.value.map((x): [string, string] => [x.group, x.value])),
        tripId: props.tripId,
        calendarDay: baseStore.calendarDay,
        comment: ''
    });
    resetAfterSend();

    eventbus.emitter.emit('notification', 'messageSent');
}

function resetAfterSend() {
    resetFirstLevel();
    resetSecondLevel();
    resetThirdLevel();
}

function resetFirstLevel() {
    setSubjectItems();
}

function resetSecondLevel() {
    messageItems.value = [];
    selectedSubject.value = null;
}

function resetThirdLevel() {
    groupedChoiceItems.value = [];
    selectedChoiceItems.value = [];
    inputItems.value.forEach(item => {
        item.value = '';
    });
    inputItems.value = [];
}

function navigateMpc() {
    router.push({
        name: 'trip',
        params: { lineNumber: props.lineNumber, tripId: props.tripId, stopIndex: '0' },
        query: { token: router.currentRoute.value.query.token, view: Views[ViewIndex.mpc], date: route.query.date }
    });
}

initialize();
</script>
<style lang="scss">
.col-6 {
    .radio-button-list {
        display: block;
    }
}

.radio-button-list {
    flex-direction: column;

    &.inline {
        flex-direction: row;
    }
}

.v-btn--disabled.bg-primary {
    background-color: #ffffff !important;
    color: rgba(0, 0, 0, 0.2) !important;
    border: thin solid currentColor !important;

    .v-btn__overlay {
        display: none;
    }
}

@media (max-width: 480px) {
    .nav-controls {
        .v-btn.v-size--large {
            font-size: 0.775rem;
        }
    }
}
</style>
@/features/shared/ButtonListItem @/services/emitter@/views/navigate
